import { Clear, FilterAlt } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { GetEmployeesRequest, PlaceNode } from '@ulysses-inc/harami_api_client'
import React, { useEffect, useState } from 'react'
import { useDebounce } from 'src/exShared/hooks/useDebounce'
import { usePlaceLoadingStatus } from 'src/exShared/hooks/usePlaceLoadingStatus'
import { splitAccessiblePlaceNode } from 'src/exShared/libraries/filterDropDown/place'
import { GetTemplatesFilter } from 'src/exShared/types/types'
import { FlatNode } from 'src/exShared/util/place/flattenNodePath'
import { splitLoginPlaceUUIDs } from 'src/exShared/util/place/splitLoginPlace'
import { getFilter } from 'src/exShared/util/template/getFilter'
import { useListFilter } from 'src/hooks/filter/useListFilter'
import { TemplatesFilter } from 'src/views/types/utils/ListFilter'

interface OwnProps {
  places: PlaceNode[]
  placeNodes: PlaceNode[]
  getFilterTemplates: (filter?: GetTemplatesFilter) => void
  flattenNodePath: FlatNode[]
  request?: GetEmployeesRequest
}

const TemplatesFilterDropDown: React.FC<OwnProps> = (props: OwnProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = anchorEl !== null
  const [placeNodeIds, setPlaceNodeIds] = useState<string[]>([])
  const [placeGroupNodeIds, setPlaceGroupNodeIds] = useState<string[]>([])
  const { filters, setListFilter } = useListFilter()
  const [templateName, setTemplateName] = useState<string>('')
  const { loginPlaceGroupIds, loginPlaceIds } = splitLoginPlaceUUIDs()

  const debounce = useDebounce(300)
  const placeLoadingStatus = usePlaceLoadingStatus()

  const saveFilter = (conditions: TemplatesFilter) => {
    setListFilter({
      ...(filters ?? {}),
      templates: {
        pagination: props.request,
        filter: conditions,
      },
    })
  }

  const getTemplateFilter = (conditions: TemplatesFilter) => {
    saveFilter(conditions)
    return getFilter(props.placeNodes, props.flattenNodePath, conditions)
  }

  const isFilterOn = () => {
    return (
      templateName || placeGroupNodeIds.length > 0 || placeNodeIds.length > 0
    )
  }

  const clearFilter = () => {
    const conditions = {
      templateName: '',
      placeNodeIds: loginPlaceIds,
      placeGroupNodeIds: loginPlaceGroupIds,
    }
    const filter = getTemplateFilter(conditions)
    props.getFilterTemplates(filter)
    setTemplateName('')
    setPlaceNodeIds([])
    setPlaceGroupNodeIds([])
    saveFilter(conditions)
  }

  useEffect(() => {
    const loginPlaceUUIDs = splitLoginPlaceUUIDs()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setPlaceNodeIds(loginPlaceUUIDs.loginPlaceIds)
    setPlaceGroupNodeIds(loginPlaceUUIDs.loginPlaceGroupIds)
  }, [])

  useEffect(() => {
    if (placeLoadingStatus !== 'finish') {
      return
    }
    // MEMO: 以下の条件に合致する動作がないので、不要かも知れません。
    if (!filters || !filters.templates) {
      const filter =
        loginPlaceIds.length || loginPlaceGroupIds
          ? getTemplateFilter({
              placeNodeIds: loginPlaceIds,
              placeGroupNodeIds: loginPlaceGroupIds,
            })
          : undefined
      // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
      setTemplateName('')
      props.getFilterTemplates(filter)
      setPlaceNodeIds(loginPlaceIds || [])
      setPlaceGroupNodeIds(loginPlaceGroupIds || [])
      return
    }
    const templatesFilter = filters.templates.filter
    let filterPlaceNodeIds: string[] = []
    let filterPlaceGroupNodeIds: string[] = []
    if (
      (templatesFilter?.placeNodeIds ?? []).length > 0 ||
      (templatesFilter?.placeGroupNodeIds ?? []).length > 0
    ) {
      filterPlaceNodeIds = templatesFilter?.placeNodeIds ?? []
      filterPlaceGroupNodeIds = templatesFilter?.placeGroupNodeIds ?? []
    } else {
      filterPlaceNodeIds = loginPlaceIds
      filterPlaceGroupNodeIds = loginPlaceGroupIds
    }
    setTemplateName(filters.templates.filter?.templateName ?? '')
    setPlaceNodeIds(filterPlaceNodeIds)
    setPlaceGroupNodeIds(filterPlaceGroupNodeIds)
    const filter = getTemplateFilter({
      templateName: filters.templates.filter?.templateName,
      placeNodeIds: filterPlaceNodeIds,
      placeGroupNodeIds: filterPlaceGroupNodeIds,
    })
    props.getFilterTemplates(filter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeLoadingStatus])

  const accessiblePlaceNode = splitAccessiblePlaceNode(
    props.placeNodes,
    props.places,
  )

  const handleTemplateNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTemplateName(e.target.value)
    debounce(() => {
      const filter = getTemplateFilter({
        templateName: e.target.value,
        placeNodeIds: placeNodeIds,
        placeGroupNodeIds: placeGroupNodeIds,
      })
      props.getFilterTemplates(filter)
    })
  }

  const placeGroupOptions = accessiblePlaceNode.accessiblePlaceGroups
  const placeOptions = accessiblePlaceNode.accessiblePlaces

  const selectedPlaceGroupNodes = placeGroupOptions.filter(
    node => node.uuid !== undefined && placeGroupNodeIds.includes(node.uuid),
  )
  const selectedPlaceNodes = placeOptions.filter(
    node => node.uuid !== undefined && placeNodeIds.includes(node.uuid),
  )

  const hoverArea = (
    <Stack spacing={2} p={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button onClick={clearFilter}>クリア</Button>
      </Stack>
      <Stack spacing={1}>
        <Typography variant="subtitle1">ひな形名</Typography>
        <OutlinedInput
          fullWidth
          size="small"
          onChange={handleTemplateNameChange}
          value={templateName}
          endAdornment={
            templateName && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="ひな形名をクリア"
                  onClick={() => {
                    setTemplateName('')
                    debounce(() => {
                      const filter = getTemplateFilter({
                        templateName: '',
                        placeNodeIds: placeNodeIds,
                        placeGroupNodeIds: placeGroupNodeIds,
                      })
                      props.getFilterTemplates(filter)
                    })
                  }}
                  size="small"
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }
        />
      </Stack>
      <Stack spacing={1}>
        <Typography variant="subtitle1">現場グループ名</Typography>
        <Autocomplete<PlaceNode, true, false, false>
          multiple
          disableCloseOnSelect
          size="small"
          options={placeGroupOptions}
          getOptionLabel={option => option.placeGroup?.name ?? ''}
          value={selectedPlaceGroupNodes}
          isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
          onChange={(_, newValue) => {
            const ids = newValue
              .map(node => node.uuid)
              .filter((uuid): uuid is string => uuid !== undefined)
            setPlaceGroupNodeIds(ids)
            const filter = getTemplateFilter({
              templateName: templateName,
              placeNodeIds: placeNodeIds,
              placeGroupNodeIds: ids,
            })
            props.getFilterTemplates(filter)
          }}
          renderOption={(optProps, option, { selected }) => {
            const { key, ...rest } = optProps
            return (
              <li key={key} {...rest}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox size="small" checked={selected} sx={{ p: 0 }} />
                  <Typography variant="body2">
                    {option.placeGroup?.name}
                  </Typography>
                </Stack>
              </li>
            )
          }}
          renderInput={params => <TextField {...params} />}
        />
      </Stack>
      <Stack spacing={1}>
        <Typography variant="subtitle1">現場名</Typography>
        <Autocomplete<PlaceNode, true, false, false>
          multiple
          disableCloseOnSelect
          size="small"
          options={placeOptions}
          getOptionLabel={option => option.place?.name ?? ''}
          value={selectedPlaceNodes}
          isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
          onChange={(_, newValue) => {
            const ids = newValue
              .map(node => node.uuid)
              .filter((uuid): uuid is string => uuid !== undefined)
            setPlaceNodeIds(ids)
            const filter = getTemplateFilter({
              templateName: templateName,
              placeNodeIds: ids,
              placeGroupNodeIds: placeGroupNodeIds,
            })
            props.getFilterTemplates(filter)
          }}
          renderOption={(optProps, option, { selected }) => {
            const { key, ...rest } = optProps
            return (
              <li key={key} {...rest}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox size="small" checked={selected} sx={{ p: 0 }} />
                  <Typography variant="body2">{option.place?.name}</Typography>
                </Stack>
              </li>
            )
          }}
          renderInput={params => <TextField {...params} />}
        />
      </Stack>
    </Stack>
  )

  return (
    <Box>
      <Button
        color={isFilterOn() ? 'filtered' : 'secondary'}
        variant="outlined"
        startIcon={<FilterAlt />}
        onClick={({ currentTarget }) => {
          setAnchorEl(currentTarget)
        }}
      >
        絞り込み: {isFilterOn() ? 'ON' : 'OFF'}
      </Button>
      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { width: '100%', maxWidth: 432 },
          },
        }}
      >
        {hoverArea}
      </Popover>
    </Box>
  )
}

export default TemplatesFilterDropDown
